import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { byPinnedThenUpdated, listProjects, updateProject, type Project } from '../lib/projects';
import { isProjectStatus, type ProjectStatus } from '../lib/projectStatus';
import { isProjectView, readStoredView, storeView, type ProjectView } from '../lib/projectView';
import { ProjectCard } from '../components/projects/ProjectCard';
import { ProjectRow } from '../components/projects/ProjectRow';
import { StatusFilter } from '../components/projects/StatusFilter';
import {
  Alert,
  Button,
  CountBadge,
  EmptyState,
  LinkButton,
  PageHeader,
  SEGMENTED_TRACK,
  segmentedItem,
  SkeletonCards,
  SkeletonList,
} from '../components/ui';
import { FolderIcon, GridIcon, ListIcon, PlusIcon } from '../components/icons';
import { useToast } from '../components/toast';

/**
 * The status filter lives in the URL so a filtered view can be shared and the
 * back button behaves. The view toggle does too, but falls back to the reader's
 * stored preference when the URL says nothing — so a shared link still opens in
 * the view its sender meant, while a plain visit reopens in the view you left.
 */
export function Projects() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  /** A failed load has no list to show, so it stands in for one. A failed pin
   *  happens over a list that is still on screen and still correct -- replacing
   *  it with a red sentence takes away the card you just clicked. Same split as
   *  the tasks page. */
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [preferredView, setPreferredView] = useState<ProjectView>(readStoredView);
  const [pinningId, setPinningId] = useState<string | null>(null);
  const { toast } = useToast();

  const viewParam = searchParams.get('view');
  const view: ProjectView = isProjectView(viewParam) ? viewParam : preferredView;
  const statusParam = searchParams.get('status') ?? '';
  const statuses = statusParam.split(',').filter(isProjectStatus);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);

    listProjects({ status: statuses }, controller.signal)
      .then((data) => {
        setProjects(data.projects);
        setLoadError(null);
      })
      .catch((requestError: unknown) => {
        if (controller.signal.aborted) return;
        setLoadError(
          requestError instanceof Error ? requestError.message : 'Could not load projects.',
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
    // Re-fetch whenever the status filter changes; the view toggle is client-side only.
  }, [statusParam]);

  /** Pin/unpin in place. The server sorts pinned first on load, so the only
   *  re-sort needed is the one right after a toggle. */
  async function togglePin(project: Project) {
    setPinningId(project.id);
    try {
      const { project: updated } = await updateProject(project.id, { pinned: !project.pinned });
      setProjects((current) =>
        // .map() first: sort() mutates, and the state array must not be touched.
        current.map((item) => (item.id === updated.id ? updated : item)).sort(byPinnedThenUpdated),
      );
      setActionError(null);
      // The row does not move far and nothing else changes, so without this the
      // only evidence the click landed is a small icon filling in.
      toast(updated.pinned ? `Pinned ${updated.name}` : `Unpinned ${updated.name}`);
    } catch (toggleError: unknown) {
      setActionError(
        toggleError instanceof Error ? toggleError.message : 'Could not update this project.',
      );
    } finally {
      setPinningId(null);
    }
  }

  function updateParams(changes: Record<string, string>) {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(changes)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    setSearchParams(next, { replace: true });
  }

  function selectView(next: ProjectView) {
    setPreferredView(next);
    storeView(next);
    // 'card' is the default, so it stays out of the URL rather than pinning a
    // value that would override the preference on a later visit.
    updateParams({ view: next === 'card' ? '' : next });
  }

  return (
    <div>
      <PageHeader
        title="Projects"
        meta={!loading && !loadError ? <CountBadge>{projects.length}</CountBadge> : null}
        actions={
          <LinkButton variant="primary" to="/projects/new">
            <PlusIcon />
            New project
          </LinkButton>
        }
      />

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <StatusFilter
          selected={statuses}
          onChange={(next: ProjectStatus[]) => updateParams({ status: next.join(',') })}
        />

        <div role="group" aria-label="Layout" className={SEGMENTED_TRACK}>
          <ViewToggleButton
            active={view === 'card'}
            label="Card view"
            onClick={() => selectView('card')}
          >
            <GridIcon className="h-4 w-4" />
          </ViewToggleButton>
          <ViewToggleButton
            active={view === 'list'}
            label="List view"
            onClick={() => selectView('list')}
          >
            <ListIcon className="h-4 w-4" />
          </ViewToggleButton>
        </div>
      </div>

      {/* Above the list, never instead of it -- see `actionError`. */}
      {actionError ? (
        <div className="mt-4">
          <Alert onDismiss={() => setActionError(null)}>{actionError}</Alert>
        </div>
      ) : null}

      <div className="mt-6">
        {loadError ? (
          <Alert>{loadError}</Alert>
        ) : loading ? (
          view === 'card' ? (
            <SkeletonCards />
          ) : (
            <SkeletonList rows={6} />
          )
        ) : projects.length === 0 ? (
          <EmptyState
            icon={<FolderIcon className="h-5 w-5" />}
            title={statuses.length > 0 ? 'No matching projects' : 'No projects yet'}
            action={
              statuses.length > 0 ? (
                <Button onClick={() => updateParams({ status: '' })}>Clear filter</Button>
              ) : (
                <LinkButton variant="primary" to="/projects/new">
                  <PlusIcon />
                  New project
                </LinkButton>
              )
            }
          >
            {statuses.length > 0
              ? 'Nothing is at any of the selected stages.'
              : 'Projects you create will be listed here.'}
          </EmptyState>
        ) : view === 'card' ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {projects.map((project) => (
              <ProjectCard
                key={project.id}
                project={project}
                pinning={pinningId === project.id}
                onTogglePin={(target) => void togglePin(target)}
              />
            ))}
          </div>
        ) : (
          <ul className="divide-y divide-stone-100 overflow-hidden rounded-xl border border-stone-200 bg-white shadow-sm">
            {projects.map((project) => (
              <ProjectRow
                key={project.id}
                project={project}
                pinning={pinningId === project.id}
                onTogglePin={(target) => void togglePin(target)}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function ViewToggleButton({
  active,
  label,
  onClick,
  children,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={active}
      onClick={onClick}
      // `segmentedItem` with the horizontal padding trimmed: these are square
      // icon buttons, and the shared text padding would leave a lot of air
      // around a 16px glyph.
      className={`${segmentedItem(active)} px-2 py-1.5`}
    >
      {children}
    </button>
  );
}

